// Editor Constants and Helpers

// Element types
export const ELEMENT_TYPES = {
  TEXT: "text",
  PEDAGOGICAL_TEXT: "pedagogical_text",
  RECT: "rect",
  CIRCLE: "circle",
  IMAGE: "image",
  SEYES: "seyes",
  GRID: "grid",
  TABLE: "table",
  QRCODE: "qrcode",
  AUDIO: "audio",
  QCM_INTERACTIVE: "qcm_interactive",
};

// Default element properties
export const DEFAULT_PROPS = {
  [ELEMENT_TYPES.TEXT]: {
    text: "Appuyer pour editer",
    fontSize: 24,
    fontFamily: "Nunito",
    fill: "#1e293b",
    width: 200,
    align: "left",
  },
  [ELEMENT_TYPES.PEDAGOGICAL_TEXT]: {
    text: "Texte pedagogique",
    fontSize: 20,
    fontFamily: "Nunito",
    fill: "#1e293b",
    width: 280,
    align: "left",
    lineHeight: 1.6,
    letterSpacing: 0.02,
    wordSpacing: 0.2,
    isPedagogical: true,
    showColoredSyllables: true,
    showSilentLetters: true,
    showGraphemeHighlight: false,
    showLineAlternation: false,
    readingAidLevel: "medium",
  },
  [ELEMENT_TYPES.RECT]: {
    width: 120,
    height: 80,
    fill: "#3b82f6",
    stroke: "#1d4ed8",
    strokeWidth: 2,
    cornerRadius: 8,
  },
  [ELEMENT_TYPES.CIRCLE]: {
    radius: 40,
    fill: "#f97316",
    stroke: "#ea580c",
    strokeWidth: 2,
  },
  [ELEMENT_TYPES.SEYES]: {
    width: 200,
    height: 120,
    lineColor: "#c4b5fd",
    marginColor: "#f87171",
    lineHeight: 8,
    showMargin: true,
  },
  [ELEMENT_TYPES.GRID]: {
    width: 160,
    height: 160,
    cellSize: 20,
    strokeColor: "#94a3b8",
    strokeWidth: 1,
    gridType: "square",
  },
  [ELEMENT_TYPES.TABLE]: {
    width: 240,
    height: 120,
    rows: 3,
    cols: 3,
    strokeColor: "#1e293b",
    strokeWidth: 2,
    fillColor: "#ffffff",
    headerColor: "#dbeafe",
  },
  [ELEMENT_TYPES.QRCODE]: {
    width: 100,
    height: 100,
    qrData: "https://example.com",
    bgColor: "#ffffff",
    fgColor: "#000000",
  },
  [ELEMENT_TYPES.AUDIO]: {
    width: 180,
    height: 60,
    audioType: "tts",
    text: "Bonjour !",
    audioData: null,
    bgColor: "#8b5cf6",
  },
  [ELEMENT_TYPES.QCM_INTERACTIVE]: {
    width: 280,
    height: 200,
    question: "Question ?",
    options: ["Reponse A", "Reponse B", "Reponse C"],
    correctIndex: 0,
    showFeedback: true,
    feedbackSound: true,
  },
};

// Color palette
export const COLOR_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", 
  "#8b5cf6", "#ec4899", "#1e293b", "#64748b", "#ffffff"
];

// Font options for pedagogical text
export const FONT_OPTIONS = [
  { value: "Nunito", label: "Nunito (Standard)" },
  { value: "OpenDyslexic", label: "OpenDyslexic (Dyslexie)" },
  { value: "Comic Sans MS", label: "Comic Sans (Lisible)" },
  { value: "Lexie Readable", label: "Lexie (Accessible)" },
  { value: "Fredoka", label: "Fredoka (Enfant)" },
  { value: "Georgia", label: "Georgia (Serif)" },
  { value: "Caveat", label: "Caveat (Manuscrit)" },
];

// Reading aid levels
export const READING_AID_LEVELS = [
  { value: "none", label: "Aucune aide" },
  { value: "light", label: "Aide legere" },
  { value: "medium", label: "Aide moyenne" },
  { value: "full", label: "Aide complete" },
];

// Target types
export const TARGET_TYPES = {
  GENERIC: "generic",
  STUDENT: "student",
  MULTI_STUDENT: "multi_student",
  GROUP: "group",
};

// Canvas defaults
export const CANVAS_DEFAULTS = {
  width: 350,
  height: 500,
  mobileWidth: 350,
  mobileHeight: 500,
  desktopWidth: 595,
  desktopHeight: 842,
  scale: 1,
  bgColor: "#ffffff",
};

// Toolbar tools
export const TOOLS = [
  { id: "select", icon: "MousePointer", label: "Selection", shortcut: "V" },
  { id: "text", icon: "Type", label: "Texte", type: ELEMENT_TYPES.TEXT },
  { id: "pedagogical_text", icon: "BookOpen", label: "Texte pedagogique", type: ELEMENT_TYPES.PEDAGOGICAL_TEXT },
  { id: "rect", icon: "Square", label: "Rectangle", type: ELEMENT_TYPES.RECT },
  { id: "circle", icon: "Circle", label: "Cercle", type: ELEMENT_TYPES.CIRCLE },
  { id: "image", icon: "Image", label: "Image", type: ELEMENT_TYPES.IMAGE },
  { id: "seyes", icon: "FileText", label: "Lignes Seyes", type: ELEMENT_TYPES.SEYES },
  { id: "grid", icon: "Grid3X3", label: "Quadrillage", type: ELEMENT_TYPES.GRID },
  { id: "table", icon: "Table", label: "Tableau", type: ELEMENT_TYPES.TABLE },
  { id: "qrcode", icon: "QrCode", label: "QR Code", type: ELEMENT_TYPES.QRCODE },
  { id: "audio", icon: "Volume2", label: "Audio", type: ELEMENT_TYPES.AUDIO },
  { id: "qcm", icon: "CheckCircle", label: "QCM", type: ELEMENT_TYPES.QCM_INTERACTIVE },
];

// Templates
export const TEMPLATES = [
  { 
    id: "flashcard", 
    name: "Flashcard", 
    icon: "CreditCard",
    elements: [
      { type: ELEMENT_TYPES.RECT, x: 50, y: 50, width: 250, height: 150, fill: "#ffffff", stroke: "#3b82f6", strokeWidth: 3, cornerRadius: 16 },
      { type: ELEMENT_TYPES.TEXT, x: 80, y: 100, text: "Question ?", fontSize: 24, fill: "#1e293b", fontFamily: "Fredoka" },
    ]
  },
  { 
    id: "pedagogical", 
    name: "Texte adapte", 
    icon: "BookOpen",
    elements: [
      { type: ELEMENT_TYPES.PEDAGOGICAL_TEXT, x: 30, y: 30, text: "Il etait une fois un petit garcon qui aimait lire.", fontSize: 20, fill: "#1e293b", width: 290, isPedagogical: true, showColoredSyllables: true, showSilentLetters: true },
    ]
  },
  { 
    id: "seyes_writing", 
    name: "Ecriture Seyes", 
    icon: "FileText",
    elements: [
      { type: ELEMENT_TYPES.SEYES, x: 25, y: 50, width: 300, height: 200, lineHeight: 8, showMargin: true },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 30, text: "Copie :", fontSize: 16, fill: "#64748b" },
    ]
  },
  { 
    id: "math_grid", 
    name: "Quadrillage Math", 
    icon: "Grid3X3",
    elements: [
      { type: ELEMENT_TYPES.GRID, x: 50, y: 80, width: 200, height: 200, cellSize: 20, gridType: "square" },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 40, text: "Exercice :", fontSize: 16, fill: "#64748b" },
    ]
  },
  {
    id: "qcm_template",
    name: "QCM Interactif",
    icon: "CheckCircle",
    elements: [
      { type: ELEMENT_TYPES.QCM_INTERACTIVE, x: 35, y: 50, width: 280, height: 200, question: "Quelle est la capitale de la France ?", options: ["Lyon", "Paris", "Marseille"], correctIndex: 1 },
    ]
  },
];

// Clipart categories
export const CLIPART_CATEGORIES = [
  { id: "consignes", label: "Consignes", items: ["read", "write", "listen", "speak", "draw", "cut", "paste", "color"] },
  { id: "emotions", label: "Emotions", items: ["happy", "sad", "angry", "surprised", "thinking", "star", "heart", "thumbsup"] },
  { id: "school", label: "Ecole", items: ["pencil", "book", "notebook", "ruler", "scissors", "backpack", "clock", "calendar"] },
  { id: "nature", label: "Nature", items: ["sun", "cloud", "rain", "tree", "flower", "leaf", "bird", "butterfly"] },
  { id: "animals", label: "Animaux", items: ["cat", "dog", "rabbit", "fish", "bird", "elephant", "lion", "bear"] },
];

// Helper functions
export const generateId = () => {
  return `elem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const createElement = (type, x, y, customProps = {}) => {
  const defaults = DEFAULT_PROPS[type] || {};
  return {
    id: generateId(),
    type,
    x,
    y,
    ...defaults,
    ...customProps,
    locked: false,
    visible: true,
  };
};

export const cloneElement = (element) => {
  return {
    ...element,
    id: generateId(),
    x: element.x + 20,
    y: element.y + 20,
  };
};

export const getElementBounds = (element) => {
  const width = element.width || element.radius * 2 || 100;
  const height = element.height || element.radius * 2 || 100;
  return {
    x: element.x,
    y: element.y,
    width,
    height,
    centerX: element.x + width / 2,
    centerY: element.y + height / 2,
  };
};
